import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    setBalance,
    setCitiesBalances,
    setPendingOrders,
    setPendingRootTransactions,
    setPendingTransactions,
    setStudentsBalances,
    setTeachersBalances,
    setCountriesBalances,
    setToken,
} from '../../actions/bank';
import { openRequestModal } from '../../actions/modals';
import { getInfoTranslate } from '../../actions/toast';
import BankCustomersService from '../../services/bank-customers.service';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReplyIcon from '@mui/icons-material/Reply';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Button, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import style from './BalanceComponent.module.css';
import CustomLoadingSkeleton from './CustomLoadingSkeleton';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export default function BalanceComponent(props) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingBalance, setLoadingBalance] = useState(false);

    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector(state => state.bank);
    const { info, emulation } = useSelector(state => state.user);
    const role_id = emulation.length ? emulation[emulation.length - 1].info.role_id : info.role_id;
    const userId = info.user_id;
    const balance = user.customer.balance || 0;
    const vip = user.customer.isvip;
    const teachersBalances = user.customer.teachersBalances || [];
    const studentsBalances = user.customer.studentsBalances || [];
    const citiesBalances = user.customer.citiesBalances || [];
    const countriesBalances = user.customer.countriesBalances || [];

    const totalTeachersBalances = useMemo(() => {
        return teachersBalances.reduce((acc, item) => acc + Number(item.teacher_balance), 0);
    }, [teachersBalances]);

    const totalStudentsBalances = useMemo(() => {
        return studentsBalances.reduce((acc, item) => acc + Number(item.student_balance), 0);
    }, [studentsBalances]);

    const totalCitiesBalances = useMemo(() => {
        return citiesBalances.reduce(
            (acc, item) =>
                acc + (Number(item.franch_balance) + Number(item.teach_balance) + Number(item.student_balance)),
            0
        );
    }, [citiesBalances]);

    const totalCountriesBalances = useMemo(() => {
        return countriesBalances.reduce((acc, item) => acc + Number(item.balance), 0);
    }, [countriesBalances]);

    const numberOfPendingTransactions = useMemo(() => {
        if (role_id === 4) {
            return user.customer.pendingTransactions ? user.customer.pendingTransactions.length || '0' : '0';
        }
        if (role_id === 2 || role_id === 3) {
            return user.customer.pendingRootTransactions ? user.customer.pendingRootTransactions.length || '0' : '0';
        }
        return '0';
    }, [role_id, user.customer.pendingTransactions, user.customer.pendingRootTransactions]);

    const numberOfPendingOrders = useMemo(() => {
        return user.customer.pendingOrders ? user.customer.pendingOrders.length || '0' : '0';
    }, [user.customer.pendingOrders]);

    const { classes } = props;

    const getCheckExist = async (noLoadingState = true) => {
        const res = await BankCustomersService.GetCheckExist();
        if (res && res.jwt) {
            setIsLoggedIn(noLoadingState);
            dispatch(setToken(res));
            fetchAllData(noLoadingState);
        } else {
            getInfoTranslate('shopPage.errorUnauthorized');
        }
        setTimeout(() => {
            setLoading(false);
        }, 300);
    };

    useEffect(() => {
        getCheckExist();
    }, []);

    const fetchAllData = async (noLoadingState = true) => {
        if (role_id === 4) {
            await dispatch(setPendingTransactions());
            await dispatch(setPendingOrders());
            await dispatch(setStudentsBalances());
        }

        if (role_id === 3) {
            await dispatch(setStudentsBalances());
            await dispatch(setTeachersBalances());
            await dispatch(setPendingOrders());
        }

        if (role_id === 2 || role_id === 3) {
            await dispatch(setPendingRootTransactions());
        }

        if (role_id === 2) {
            await dispatch(setCitiesBalances());
        }
        if (role_id === 1) {
            await dispatch(setCountriesBalances());
        }
        await fetchBalance(noLoadingState);
    };

    const onRegisterButtonClick = () => {
        const fetchUserData = async () => {
            const res = await BankCustomersService.Registration();
            if (res && res.jwt) {
                setIsLoggedIn(true);
                dispatch(setToken(res));
            }
        };
        fetchUserData();
    };

    const fetchBalance = async (noLoadingState = true) => {
        setLoadingBalance(noLoadingState);
        await dispatch(setBalance());
        setLoadingBalance(false);
    };

    async function handleClick() {
        setLoading(true);
        setLoadingBalance(true);
        await fetchAllData(false);
        setTimeout(() => {
            setLoading(false);
            setLoadingBalance(false);
        }, 500);
    }

    const handleOpenRequestModal = data => {
        dispatch(
            openRequestModal({
                opened: true,
                data,
            })
        );
    };

    const handleOpenBalancesPage = path => {
        history.push(path);
    };

    const formattedNumber = useCallback(number => {
        return new Intl.NumberFormat(undefined, {
            maximumFractionDigits: 2,
        }).format(number);
    }, []);

    return (
        <>
            <Paper elevation={0} style={{ position: 'relative', marginBottom: '20px' }}>
                <Box className={style.container}>
                    <Typography variant={'caption'} className={style.caption}>
                        Soroban BANK
                    </Typography>
                </Box>
                <Collapse in={loading}>
                    <Box p={2} m={2}>
                        <CustomLoadingSkeleton />
                    </Box>
                </Collapse>
                <Collapse in={!loading && !loadingBalance}>
                    <Box m={2} p={2}>
                        {isLoggedIn ? (
                            <div>
                                {role_id === 5 && vip && <Box className={style.vipStatus}>VIP</Box>}
                                <div className={style.balanceContainer}>
                                    <Box className={style.balanceWrapper}>
                                        <Typography
                                            component="div"
                                            style={{
                                                width: '100px',
                                                marginRight: '7px',
                                            }}
                                        >
                                            {t('balanceComponent.balance')}
                                        </Typography>
                                        <Box>
                                            <Typography className={style.currentBalance}>
                                                {loadingBalance ? (
                                                    <CircularProgress size={13} />
                                                ) : (
                                                    formattedNumber(balance)
                                                )}{' '}
                                            </Typography>
                                            <Typography className={style.currencyLabel} variant={'caption'}>
                                                Sorocoins
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row">
                                        <Tooltip title={t('balanceComponent.updateBalance')} placement="bottom">
                                            <span>
                                                <IconButton
                                                    className={style.updateBalanceButton}
                                                    onClick={handleClick}
                                                    disabled={loadingBalance || loading}
                                                    aria-label={t('balanceComponent.updateBalance')}
                                                >
                                                    <UpdateIcon
                                                        style={{
                                                            color: '#58a315',
                                                        }}
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip
                                            title={t('transactionsHistory.modalTitleTransactionsHistory')}
                                            placement="bottom"
                                        >
                                            <IconButton
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '20px',
                                                }}
                                                aria-label={t('transactionsHistory.modalTitleTransactionsHistory')}
                                                onClick={() => {
                                                    handleOpenRequestModal({
                                                        header: t('transactionsHistory.modalTitleTransactionsHistory'),
                                                        modaltype: 'TransactionHistory',
                                                    });
                                                }}
                                            >
                                                <ReceiptLongIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {role_id !== 1 && role_id !== 2 ? (
                                            <Tooltip
                                                title={t('transactionsHistory.modalTitleOrderHistory')}
                                                placement="bottom"
                                            >
                                                <IconButton
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '20px',
                                                    }}
                                                    aria-label={t('transactionsHistory.modalTitleOrderHistory')}
                                                    onClick={() => {
                                                        handleOpenRequestModal({
                                                            header: t('transactionsHistory.modalTitleOrderHistory'),
                                                            modaltype: 'OrderHistory',
                                                        });
                                                    }}
                                                >
                                                    <ShoppingBagIcon style={{ color: '#58a315' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                        {role_id !== 1 && role_id !== 2 && role_id !== 3 ? (
                                            <Tooltip title={t('transactionsHistory.moneybox')} placement="bottom">
                                                <IconButton
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '20px',
                                                    }}
                                                    aria-label={t('transactionsHistory.moneybox')}
                                                    onClick={() => {
                                                        handleOpenRequestModal({
                                                            header: t('transactionsHistory.moneybox'),
                                                            modaltype: 'MoneyBox',
                                                        });
                                                    }}
                                                >
                                                    <MonetizationOnIcon style={{ color: '#58a315' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                        {role_id === 4 ? (
                                            <Tooltip title={t('transactionsHistory.quests')} placement="bottom">
                                                <IconButton
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '20px',
                                                    }}
                                                    aria-label={t('transactionsHistory.quests')}
                                                    onClick={() => {
                                                        handleOpenRequestModal({
                                                            header: t('transactionsHistory.quests'),
                                                            modaltype: 'Quests',
                                                        });
                                                    }}
                                                >
                                                    <FactCheckIcon style={{ color: '#58a315' }} />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </Stack>
                                </div>

                                {role_id === 1 && (
                                    <Button
                                        className={style.buttonOpenBalancesPage}
                                        variant="outlined"
                                        onClick={() => handleOpenBalancesPage('/dashboard/balances/boss')}
                                        startIcon={<AttachMoneyIcon />}
                                    >
                                        {t('balanceComponent.bosses')}{' '}
                                        <span className={style.balanceSummary}>
                                            {formattedNumber(totalCountriesBalances)}
                                        </span>
                                        SBK
                                    </Button>
                                )}

                                {role_id === 2 && (
                                    <Button
                                        className={style.buttonOpenBalancesPage}
                                        variant="outlined"
                                        onClick={() => handleOpenBalancesPage('/dashboard/balances/franch')}
                                        startIcon={<AttachMoneyIcon />}
                                    >
                                        {t('balanceComponent.cities')}{' '}
                                        <span className={style.balanceSummary}>
                                            {formattedNumber(totalCitiesBalances)}
                                        </span>
                                        SBK
                                    </Button>
                                )}

                                {role_id === 3 ? (
                                    <div className={style.flexContainer}>
                                        <Button
                                            className={style.buttonOpenBalancesPage}
                                            variant="outlined"
                                            onClick={() => handleOpenBalancesPage('/dashboard/balances/teachers')}
                                            startIcon={<AttachMoneyIcon />}
                                        >
                                            {t('balanceComponent.teachers')}{' '}
                                            <span className={style.balanceSummary}>
                                                {formattedNumber(totalTeachersBalances)}
                                            </span>
                                            SBK
                                        </Button>
                                        <Button
                                            className={style.buttonOpenBalancesPage}
                                            variant="outlined"
                                            onClick={() => handleOpenBalancesPage('/dashboard/balances/students')}
                                            startIcon={<AttachMoneyIcon />}
                                        >
                                            {t('balanceComponent.students')}{' '}
                                            <span className={style.balanceSummary}>
                                                {formattedNumber(totalStudentsBalances)}
                                            </span>
                                            SBK
                                        </Button>
                                    </div>
                                ) : null}

                                {role_id === 4 ? (
                                    <div className={style.flexContainer}>
                                        <Button
                                            variant="outlined"
                                            className={style.buttonOpenBalancesPage}
                                            onClick={() => handleOpenBalancesPage('/dashboard/balances/students')}
                                            startIcon={<AttachMoneyIcon />}
                                        >
                                            {t('balanceComponent.students')}{' '}
                                            <span className={style.balanceSummary}>
                                                {formattedNumber(totalStudentsBalances)}
                                            </span>
                                            SBK
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            className={style.buttonOpenBalancesPage}
                                            onClick={() =>
                                                handleOpenBalancesPage('/dashboard/balances/villageSettings')
                                            }
                                            startIcon={<SettingsIcon />}
                                        >
                                            {t('gameSettings.village')}
                                        </Button>
                                    </div>
                                ) : null}

                                {role_id === 2 || role_id === 3 || role_id === 4 ? (
                                    <Stack className={style.transactionsContainer}>
                                        <Typography>{t('balanceComponent.confirmationAwaited')}</Typography>
                                        <Button
                                            className={style.buttonOpenModalActiveTransactions}
                                            onClick={() => {
                                                if (role_id === 4) {
                                                    handleOpenRequestModal({
                                                        id: userId,
                                                        header: t('balanceComponent.titleModalAllActiveTransactions'),
                                                        modaltype: 'AllActiveTransactions',
                                                    });
                                                } else if (role_id === 2 || role_id === 3) {
                                                    handleOpenRequestModal({
                                                        id: userId,
                                                        header: t('balanceComponent.titleModalActiveRootTransactions'),
                                                        modaltype: 'AllActiveRootTransactions',
                                                    });
                                                }
                                            }}
                                        >
                                            {numberOfPendingTransactions || (
                                                <CircularProgress color={'error'} size={10} />
                                            )}{' '}
                                            {t('balanceComponent.transactionsSBK')}
                                        </Button>

                                        {role_id === 3 || role_id === 4 ? (
                                            <Button
                                                className={style.buttonOpenModalActiveTransactions}
                                                onClick={() =>
                                                    handleOpenRequestModal({
                                                        id: userId,
                                                        header: t('balanceComponent.titleModalAllActiveOrders'),
                                                        modaltype: 'AllActiveOrders',
                                                    })
                                                }
                                            >
                                                {numberOfPendingOrders || (
                                                    <CircularProgress color={'error'} size={10} />
                                                )}{' '}
                                                {t('balanceComponent.goodsOrders')}
                                            </Button>
                                        ) : null}
                                    </Stack>
                                ) : null}
                                <Box direction="row" alignItems="center" justifyContent="flex-start">
                                    <Button
                                        className={style.buttonTransferSorocoins}
                                        variant="outlined"
                                        onClick={() =>
                                            handleOpenRequestModal({
                                                id: userId,
                                                header: t('balanceComponent.titleModalTransferSorocoins'),
                                                modaltype: 'TransferSorocoins',
                                            })
                                        }
                                        startIcon={<ReplyIcon />}
                                    >
                                        {t('balanceComponent.moneyTransfers')}
                                    </Button>

                                    {role_id === 3 || role_id === 4 ? (
                                        <Button
                                            className={style.buttonTransferSorocoins}
                                            variant="outlined"
                                            onClick={() =>
                                                handleOpenRequestModal({
                                                    id: userId,
                                                    header: t('balanceComponent.titleModalOrderSorocoins'),
                                                    modaltype: 'OrderSorocoins',
                                                })
                                            }
                                        >
                                            {t('balanceComponent.orderSoroCoins')}
                                        </Button>
                                    ) : null}

                                    {role_id === 5 ? (
                                        <>
                                            {/* <Button
                                                className={
                                                    style.buttonDepositMoney
                                                }
                                                variant="contained"
                                                onClick={() =>
                                                    handleOpenRequestModal({
                                                        id: userId,
                                                        header: t(
                                                            'balanceComponent.titleModalDepositMoney'
                                                        ),
                                                        modaltype:
                                                            'DepositMoney',
                                                    })
                                                }
                                                startIcon={<AttachMoneyIcon />}
                                            >
                                                {t(
                                                    'balanceComponent.depositMoney'
                                                )}
                                            </Button> */}
                                            <Button
                                                className={style.buttonDepositMoney}
                                                variant="contained"
                                                color={'error'}
                                                onClick={() =>
                                                    handleOpenRequestModal({
                                                        id: userId,
                                                        header: t('balanceComponent.titleModalWithdrawMoney'),
                                                        modaltype: 'WithdrawMoney',
                                                    })
                                                }
                                                startIcon={<MoneyOffIcon />}
                                            >
                                                {t('balanceComponent.withdrawMoney')}
                                            </Button>
                                        </>
                                    ) : null}
                                </Box>
                            </div>
                        ) : (
                            <Button
                                className={style.buttonRegisterInSOROBANK}
                                variant="contained"
                                startIcon={<AccountBalanceIcon />}
                                onClick={onRegisterButtonClick}
                            >
                                {t('balanceComponent.registerInSOROBANK')}
                            </Button>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        </>
    );
}
